import React from 'react';
import { createPortal } from 'react-dom';
import { shallow } from 'zustand/shallow';
import { useGameStore } from '../store/useGameStore';
// Only import the pulse key type. GameState is no longer referenced directly
// in this component; callers pass in snapshot objects instead.
import type { CombatUiStatusPulseKey } from '../game/types';

import reflectIcon from './status_reflect.png';
import negateIcon from './status_negate.png';
import stunIcon from './status_stun.png';
import discardIcon from './status_discard.png';
import chainIcon from './status_chain.png';
import delayedIcon from './status_delayed.png';
import hpIcon from './status_hp.png';
import spIcon from './status_sp.png';

type ChipTone = 'neutral' | 'enemy' | 'debuff';

// Export scopes and layouts so that Board can refer to them without
// coupling to the internals of StatusChips.
export type StatusScope = 'all' | 'player' | 'enemy';
export type StatusLayout = 'inline' | 'arc';

// Snapshot types for player and enemy status. These contain only
// the properties required to render the status chips. See Board.tsx
// for construction of these snapshots.
export type PlayerStatusSnapshot = {
  stunned: number;
  reflectPct: number;
  negateNextDamage: number;
  nextAttackBonus: number;
  nextCardCost0: number;
  playerDamageMultThisTurn: number;
  playerIncomingDamageMultNextTurn: number;
  nextMagicCardCostDoubleSP: number;
  playerWeakenedTurns: number;
  activeCompound: string;
  darkApostleCursedFateActive: boolean;
  phase: string;
  canPlay: boolean;
  playerPlaysThisTurn: number;
  pendingDiscardRemaining: number;
  delayedCount: number;
  delayedMinTurns: number;
  physicalEscalation: number;
  magicEscalation: number;
  custodianForfeitureTurns: number;
};

export type EnemyStatusSnapshot = {
  stunned: number;
  enemyDamageMultTurns: number;
  enemyDamageMult: number;
  reflectPct: number;
  activeCompound: string;
  mercenaryPrecision: boolean;
  mercenaryAmbush: boolean;
  lampKeeperDisposition: string;
  lampKeeperRoomLight: string;
  custodianForfeitureTurns: number;
  custodianExiledAttackCount: number;
  custodianExiledDefenseCount: number;
  custodianExiledUtilityCount: number;
  thresholdVerifierDeferredSanctionCount: number;
  thresholdVerifierDeferredSanctionMinTurns: number;
  isPrimeVessel: boolean;
  pulseEngineActive: boolean;
  synapticRetortActive: boolean;
  astralReliquaryActive: boolean;
  primeCrucibleActive: boolean;
  isCustodian: boolean;
};

// Empty defaults used when snapshots are omitted. These avoid
// undefined checks throughout the render logic.
const EMPTY_PLAYER_STATUS: PlayerStatusSnapshot = {
  stunned: 0,
  reflectPct: 0,
  negateNextDamage: 0,
  nextAttackBonus: 0,
  nextCardCost0: 0,
  playerDamageMultThisTurn: 1,
  playerIncomingDamageMultNextTurn: 1,
  nextMagicCardCostDoubleSP: 0,
  playerWeakenedTurns: 0,
  activeCompound: '',
  darkApostleCursedFateActive: false,
  phase: '',
  canPlay: false,
  playerPlaysThisTurn: 0,
  pendingDiscardRemaining: 0,
  delayedCount: 0,
  delayedMinTurns: 0,
  physicalEscalation: 0,
  magicEscalation: 0,
  custodianForfeitureTurns: 0,
};

const EMPTY_ENEMY_STATUS: EnemyStatusSnapshot = {
  stunned: 0,
  enemyDamageMultTurns: 0,
  enemyDamageMult: 1,
  reflectPct: 0,
  activeCompound: '',
  mercenaryPrecision: false,
  mercenaryAmbush: false,
  lampKeeperDisposition: '',
  lampKeeperRoomLight: '',
  custodianForfeitureTurns: 0,
  custodianExiledAttackCount: 0,
  custodianExiledDefenseCount: 0,
  custodianExiledUtilityCount: 0,
  thresholdVerifierDeferredSanctionCount: 0,
  thresholdVerifierDeferredSanctionMinTurns: 0,
  isPrimeVessel: false,
  pulseEngineActive: false,
  synapticRetortActive: false,
  astralReliquaryActive: false,
  primeCrucibleActive: false,
  isCustodian: false,
};

type ChipItem = {
  key: string;
  node: React.ReactNode;
};

type TooltipPlacement = 'top' | 'bottom';

type TooltipState = {
  key: string;
  text: string;
  x: number;
  y: number;
  placement: TooltipPlacement;
};

type TooltipController = {
  isMobile: boolean;
  pinnedKey: string | null;
  show: (key: string, text: string, el: HTMLElement | null) => void;
  hide: (key: string) => void;
  toggle: (key: string, text: string, el: HTMLElement | null) => void;
};

const TooltipCtx = React.createContext<TooltipController | null>(null);

// --- Comparator helpers for memoization ---
// These helpers compare snapshot props rather than the full GameState to
// determine whether StatusChips should re-render. They ensure that only
// relevant changes in combat state trigger an update.
function arePulseKeysEqual(
  prevPulseMap: Partial<Record<CombatUiStatusPulseKey, number>> | undefined,
  nextPulseMap: Partial<Record<CombatUiStatusPulseKey, number>> | undefined,
  keys: CombatUiStatusPulseKey[],
) {
  for (const key of keys) {
    if ((prevPulseMap?.[key] ?? 0) !== (nextPulseMap?.[key] ?? 0)) {
      return false;
    }
  }
  return true;
}

function arePlayerStatusSnapshotsEqual(
  prev: PlayerStatusSnapshot | undefined,
  next: PlayerStatusSnapshot | undefined,
) {
  const a = prev ?? EMPTY_PLAYER_STATUS;
  const b = next ?? EMPTY_PLAYER_STATUS;
  return (
    a.stunned === b.stunned &&
    a.reflectPct === b.reflectPct &&
    a.negateNextDamage === b.negateNextDamage &&
    a.nextAttackBonus === b.nextAttackBonus &&
    a.nextCardCost0 === b.nextCardCost0 &&
    a.playerDamageMultThisTurn === b.playerDamageMultThisTurn &&
    a.playerIncomingDamageMultNextTurn === b.playerIncomingDamageMultNextTurn &&
    a.nextMagicCardCostDoubleSP === b.nextMagicCardCostDoubleSP &&
    a.playerWeakenedTurns === b.playerWeakenedTurns &&
    a.activeCompound === b.activeCompound &&
    a.darkApostleCursedFateActive === b.darkApostleCursedFateActive &&
    a.phase === b.phase &&
    a.canPlay === b.canPlay &&
    a.playerPlaysThisTurn === b.playerPlaysThisTurn &&
    a.pendingDiscardRemaining === b.pendingDiscardRemaining &&
    a.delayedCount === b.delayedCount &&
    a.delayedMinTurns === b.delayedMinTurns &&
    a.physicalEscalation === b.physicalEscalation &&
    a.magicEscalation === b.magicEscalation &&
    a.custodianForfeitureTurns === b.custodianForfeitureTurns
  );
}

function areEnemyStatusSnapshotsEqual(
  prev: EnemyStatusSnapshot | undefined,
  next: EnemyStatusSnapshot | undefined,
) {
  const a = prev ?? EMPTY_ENEMY_STATUS;
  const b = next ?? EMPTY_ENEMY_STATUS;
  return (
    a.stunned === b.stunned &&
    a.enemyDamageMultTurns === b.enemyDamageMultTurns &&
    a.enemyDamageMult === b.enemyDamageMult &&
    a.reflectPct === b.reflectPct &&
    a.activeCompound === b.activeCompound &&
    a.mercenaryPrecision === b.mercenaryPrecision &&
    a.mercenaryAmbush === b.mercenaryAmbush &&
    a.lampKeeperDisposition === b.lampKeeperDisposition &&
    a.lampKeeperRoomLight === b.lampKeeperRoomLight &&
    a.custodianForfeitureTurns === b.custodianForfeitureTurns &&
    a.custodianExiledAttackCount === b.custodianExiledAttackCount &&
    a.custodianExiledDefenseCount === b.custodianExiledDefenseCount &&
    a.custodianExiledUtilityCount === b.custodianExiledUtilityCount &&
    a.thresholdVerifierDeferredSanctionCount === b.thresholdVerifierDeferredSanctionCount &&
    a.thresholdVerifierDeferredSanctionMinTurns === b.thresholdVerifierDeferredSanctionMinTurns &&
    a.isPrimeVessel === b.isPrimeVessel &&
    a.pulseEngineActive === b.pulseEngineActive &&
    a.synapticRetortActive === b.synapticRetortActive &&
    a.astralReliquaryActive === b.astralReliquaryActive &&
    a.primeCrucibleActive === b.primeCrucibleActive &&
    a.isCustodian === b.isCustodian
  );
}

const PLAYER_PULSE_KEYS: CombatUiStatusPulseKey[] = [
  'reflect',
  'negate',
  'atkBonus',
  'free',
  'dmgUp',
  'dmgDown',
  'magicDouble',
  'weaken',
];

const ENEMY_PULSE_KEYS: CombatUiStatusPulseKey[] = [
  'enemyStun',
  'enemyWeaken',
];

function areStatusChipsPropsEqualNew(prev: StatusChipsProps, next: StatusChipsProps) {
  if (prev.scope !== next.scope) return false;
  if (prev.layout !== next.layout) return false;
  if (prev.scope === 'player') {
    return (
      arePlayerStatusSnapshotsEqual(prev.playerStatus, next.playerStatus) &&
      arePulseKeysEqual(prev.pulseMap, next.pulseMap, PLAYER_PULSE_KEYS)
    );
  }
  if (prev.scope === 'enemy') {
    return (
      areEnemyStatusSnapshotsEqual(prev.enemyStatus, next.enemyStatus) &&
      arePulseKeysEqual(prev.pulseMap, next.pulseMap, ENEMY_PULSE_KEYS)
    );
  }
  return (
    arePlayerStatusSnapshotsEqual(prev.playerStatus, next.playerStatus) &&
    areEnemyStatusSnapshotsEqual(prev.enemyStatus, next.enemyStatus) &&
    arePulseKeysEqual(prev.pulseMap, next.pulseMap, [
      ...PLAYER_PULSE_KEYS,
      ...ENEMY_PULSE_KEYS,
    ])
  );
}

// Props for the StatusChips component. Instead of accepting the full
// GameState, this component accepts precomputed snapshots of the
// player and enemy statuses. The scope and layout props are optional
// and default to 'all' and 'inline'.
type StatusChipsProps = {
  playerStatus?: PlayerStatusSnapshot;
  enemyStatus?: EnemyStatusSnapshot;
  scope?: StatusScope;
  layout?: StatusLayout;
  pulseMap?: Partial<Record<CombatUiStatusPulseKey, number>>;
};

// Pulse key constants are defined alongside the memo comparator below.

/**
 * Build a stable signature for the delayed status array. We only care about
 * the count of delayed effects and the minimum turns remaining. This
 * signature allows us to cheaply compare the delayed state between renders.
 */
// legacy comparator based on full GameState (no longer used)
function _legacy_getDelayedSignature(state: any): string {
  const delayed = Array.isArray(state.delayed) ? state.delayed : [];
  if (delayed.length === 0) return '0:0';

  let minTurns = Infinity;
  for (const item of delayed) {
    const turnsLeft = Math.max(0, Number((item as any)?.turnsLeft) || 0);
    if (turnsLeft < minTurns) minTurns = turnsLeft;
  }

  return `${delayed.length}:${Number.isFinite(minTurns) ? minTurns : 0}`;
}

/**
 * The legacy comparator functions below call the top-level arePulseKeysEqual defined earlier.
 * The duplicate definition has been removed to avoid conflicts.
 */

/**
 * Check whether all player-related status chip inputs are unchanged between
 * renders. This reads the minimal subset of state required to render the
 * player's status chips and uses getDelayedSignature and arePulseKeysEqual
 * to avoid deep or structural comparisons.
 */
// legacy comparator based on full GameState (no longer used)
function _legacy_arePlayerStatusInputsEqual(
  prevState: any,
  nextState: any,
  prevPulseMap: any,
  nextPulseMap: any,
) {
  return (
    prevState.player.reflectPct === nextState.player.reflectPct &&
    prevState.player.negateNextDamage === nextState.player.negateNextDamage &&
    prevState.nextAttackBonus === nextState.nextAttackBonus &&
    prevState.nextCardCost0 === nextState.nextCardCost0 &&
    prevState.playerDamageMultThisTurn === nextState.playerDamageMultThisTurn &&
    prevState.playerIncomingDamageMultNextTurn === nextState.playerIncomingDamageMultNextTurn &&
    prevState.nextMagicCardCostDoubleSP === nextState.nextMagicCardCostDoubleSP &&
    prevState.playerWeakenedTurns === nextState.playerWeakenedTurns &&
    (prevState.pendingDiscard?.remaining ?? 0) ===
      (nextState.pendingDiscard?.remaining ?? 0) &&
    prevState.phase === nextState.phase &&
    prevState.canPlay === nextState.canPlay &&
    prevState.playerPlaysThisTurn === nextState.playerPlaysThisTurn &&
    _legacy_getDelayedSignature(prevState) === _legacy_getDelayedSignature(nextState) &&
    prevState.physicalEscalation === nextState.physicalEscalation &&
    prevState.magicEscalation === nextState.magicEscalation &&
    arePulseKeysEqual(prevPulseMap, nextPulseMap, PLAYER_PULSE_KEYS)
  );
}

/**
 * Check whether all enemy-related status chip inputs are unchanged between
 * renders. Only minimal fields related to enemy status chips are compared.
 */
// legacy comparator based on full GameState (no longer used)
function _legacy_areEnemyStatusInputsEqual(
  prevState: any,
  nextState: any,
  prevPulseMap: any,
  nextPulseMap: any,
) {
  return (
    prevState.enemy.stunned === nextState.enemy.stunned &&
    (prevState.enemyDamageMultTurns ?? 0) ===
      (nextState.enemyDamageMultTurns ?? 0) &&
    (prevState.enemyDamageMult ?? 1) === (nextState.enemyDamageMult ?? 1) &&
    arePulseKeysEqual(prevPulseMap, nextPulseMap, ENEMY_PULSE_KEYS)
  );
}

/**
 * Determine whether StatusChips should update based on changes to its props.
 * Depending on the scope (player, enemy, or all), we compare only the
 * relevant portions of state and pulse keys. When nothing relevant has
 * changed, React.memo will skip the render.
 */
// legacy comparator based on full GameState (no longer used)
function _legacy_areStatusChipsPropsEqual(prev: any, next: any) {
  if (prev.scope !== next.scope) return false;
  if (prev.layout !== next.layout) return false;

  if (prev.scope === 'player') {
    return _legacy_arePlayerStatusInputsEqual(
      prev.state,
      next.state,
      prev.pulseMap,
      next.pulseMap,
    );
  }

  if (prev.scope === 'enemy') {
    return _legacy_areEnemyStatusInputsEqual(
      prev.state,
      next.state,
      prev.pulseMap,
      next.pulseMap,
    );
  }

  // When scope is 'all', both player and enemy inputs must be unchanged
  return (
    _legacy_arePlayerStatusInputsEqual(
      prev.state,
      next.state,
      prev.pulseMap,
      next.pulseMap,
    ) &&
    _legacy_areEnemyStatusInputsEqual(
      prev.state,
      next.state,
      prev.pulseMap,
      next.pulseMap,
    )
  );
}

function clamp(n: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, n));
}

function computeTooltipState(key: string, text: string, el: HTMLElement | null): TooltipState | null {
  if (typeof window === 'undefined' || !el) return null;

  const rect = el.getBoundingClientRect();
  const pad = 16;

  // Estimate tooltip size so we can clamp the fixed-position portal tooltip
  // fully inside the viewport. This avoids left/right clipping even when the
  // chip is close to the screen edge.
  const estimatedWidth = clamp(
    Math.round(String(text ?? '').length * 7.2) + 28,
    120,
    Math.min(360, window.innerWidth - pad * 2)
  );
  const halfWidth = estimatedWidth / 2;
  const estimatedHeight = 44;

  const x = clamp(
    rect.left + rect.width / 2,
    pad + halfWidth,
    window.innerWidth - pad - halfWidth,
  );

  const canPlaceAbove = rect.top > estimatedHeight + pad + 10;
  const placement: TooltipPlacement = canPlaceAbove ? 'top' : 'bottom';
  const y = placement === 'top' ? rect.top - 10 : rect.bottom + 10;

  return { key, text, x, y, placement };
}

// Inline styles are used so we don't have to touch the global combat CSS.
// The goal is to clearly differentiate:
// - enemy statuses (warm/orange)
// - player debuffs (purple)
const TONE_STYLES: Record<ChipTone, React.CSSProperties | undefined> = {
  neutral: undefined,
  enemy: {
    color: 'rgba(255, 226, 200, 0.98)',
    borderColor: 'rgba(251, 146, 60, 0.85)',
    boxShadow: '0 0 0 1px rgba(251, 146, 60, 0.25), 0 0 10px rgba(251, 146, 60, 0.10)',
  },
  debuff: {
    color: 'rgba(237, 229, 255, 0.98)',
    borderColor: 'rgba(168, 85, 247, 0.90)',
    boxShadow: '0 0 0 1px rgba(168, 85, 247, 0.25), 0 0 10px rgba(168, 85, 247, 0.12)',
  },
};

const ICON_GLOW: Record<ChipTone, string | undefined> = {
  neutral: undefined,
  enemy: 'drop-shadow(0 0 2px rgba(251, 146, 60, 0.9)) drop-shadow(0 0 6px rgba(251, 146, 60, 0.35))',
  debuff: 'drop-shadow(0 0 2px rgba(168, 85, 247, 0.9)) drop-shadow(0 0 6px rgba(168, 85, 247, 0.35))',
};

type IconName =
  | 'reflect'
  | 'negate'
  | 'stun'
  | 'discard'
  | 'chain'
  | 'delayed'
  | 'hp'
  | 'sp'
  // Debuffs
  | 'curse'
  | 'weaken'
  | 'mark'
  | 'shadowstep'
  | 'pressurePoint'
  | 'punitiveMeasure'
  | 'unauthorizedPassage'
  | 'pulseEngine'
  | 'synapticRetort'
  | 'astralReliquary'
  | 'primeCrucible'
  // Other player effects
  | 'attackBonus'
  | 'free'
  | 'multUp'
  | 'multDown'
  | 'momentum'
  | 'causticCompound'
  | 'mindrotCompound'
  | 'ironCompound'
  | 'catalystCompound'
  | 'sedativeCompound'
  | 'precision'
  | 'ambush'
  | 'roomLightDark'
  | 'roomLightSteady'
  | 'roomLightBright';

function Icon({ name, tone = 'neutral' }: { name: IconName; tone?: ChipTone }) {
  // For non-PNG icons, we use tiny inline SVGs so the project doesn't need
  // new assets immediately. If you later add PNGs, you can swap them in here.

  if (name === 'curse') {
    return (
      <svg className="chipIcon" viewBox="0 0 24 24" aria-hidden="true" focusable="false" style={{ filter: ICON_GLOW[tone] }}>
        <path d="M12 2.5 20.2 7.25v9.5L12 21.5 3.8 16.75v-9.5L12 2.5Z" fill="currentColor" opacity="0.9" />
        <path
          d="M12 7.6a4.4 4.4 0 1 0 0 8.8 4.4 4.4 0 0 0 0-8.8Zm0 2.4a2 2 0 1 1 0 4 2 2 0 0 1 0-4Z"
          fill="#000"
          opacity="0.45"
        />
      </svg>
    );
  }

  if (name === 'weaken') {
    return (
      <svg className="chipIcon" viewBox="0 0 24 24" aria-hidden="true" focusable="false" style={{ filter: ICON_GLOW[tone] }}>
        <path d="M4 7h16v2H4V7Z" fill="currentColor" opacity="0.85" />
        <path d="M6 11h12v2H6v-2Z" fill="currentColor" opacity="0.65" />
        <path d="M8 15h8v2H8v-2Z" fill="currentColor" opacity="0.45" />
        <path d="M12 20 7.5 15.5h9L12 20Z" fill="currentColor" opacity="0.9" />
      </svg>
    );
  }

  if (name === 'attackBonus') {
    return (
      <svg className="chipIcon" viewBox="0 0 24 24" aria-hidden="true" focusable="false" style={{ filter: ICON_GLOW[tone] }}>
        <path
          d="M20.8 3.2 18.7 1.1 10.3 9.5 8.2 8.6 6.1 10.7l2.2 2.2-5 5v2.9h2.9l5-5 2.2 2.2 2.1-2.1-.9-2.1 8.3-8.4Z"
          fill="currentColor"
          opacity="0.9"
        />
      </svg>
    );
  }

  if (name === 'free') {
    return (
      <svg className="chipIcon" viewBox="0 0 24 24" aria-hidden="true" focusable="false" style={{ filter: ICON_GLOW[tone] }}>
        <path
          fillRule="evenodd"
          d="M12 2.5c5.25 0 9.5 4.25 9.5 9.5s-4.25 9.5-9.5 9.5-9.5-4.25-9.5-9.5S6.75 2.5 12 2.5Zm0 2.3a7.2 7.2 0 1 0 0 14.4 7.2 7.2 0 0 0 0-14.4Z"
          fill="currentColor"
          opacity="0.9"
        />
        <path d="M7 17 17 7" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" opacity="0.9" />
      </svg>
    );
  }

  if (name === 'multUp') {
    return (
      <svg className="chipIcon" viewBox="0 0 24 24" aria-hidden="true" focusable="false" style={{ filter: ICON_GLOW[tone] }}>
        <path d="M12 3 4.5 10.5h4V21h7V10.5h4L12 3Z" fill="currentColor" opacity="0.9" />
      </svg>
    );
  }

  if (name === 'multDown') {
    return (
      <svg className="chipIcon" viewBox="0 0 24 24" aria-hidden="true" focusable="false" style={{ filter: ICON_GLOW[tone] }}>
        <path d="M12 21 19.5 13.5h-4V3h-7v10.5h-4L12 21Z" fill="currentColor" opacity="0.9" />
      </svg>
    );
  }

  if (name === 'mark') {
    return (
      <svg className="chipIcon" viewBox="0 0 24 24" aria-hidden="true" focusable="false" style={{ filter: ICON_GLOW[tone] }}>
        <circle cx="12" cy="12" r="7.25" stroke="currentColor" strokeWidth="1.8" opacity="0.9" />
        <circle cx="12" cy="12" r="2.35" fill="currentColor" opacity="0.95" />
        <path d="M12 3v3.2M12 17.8V21M3 12h3.2M17.8 12H21M5.8 5.8l2.25 2.25M15.95 15.95l2.25 2.25M18.2 5.8l-2.25 2.25M8.05 15.95 5.8 18.2" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" opacity="0.88" />
      </svg>
    );
  }

  if (name === 'shadowstep') {
    return (
      <svg className="chipIcon" viewBox="0 0 24 24" aria-hidden="true" focusable="false" style={{ filter: ICON_GLOW[tone] }}>
        <path d="M12 3.4c3.5 0 6.6 2.05 8.45 5.1-1.85 3.05-4.95 5.1-8.45 5.1S5.4 11.55 3.55 8.5C5.4 5.45 8.5 3.4 12 3.4Z" fill="currentColor" opacity="0.14" stroke="currentColor" strokeWidth="1.6" />
        <circle cx="12" cy="8.5" r="2" fill="currentColor" opacity="0.9" />
        <path d="M6.8 19.5 14.2 12.1M12.3 19.5l4.9-4.9" stroke="currentColor" strokeWidth="2.1" strokeLinecap="round" />
      </svg>
    );
  }

  if (name === 'pressurePoint') {
    return (
      <svg className="chipIcon" viewBox="0 0 24 24" aria-hidden="true" focusable="false" style={{ filter: ICON_GLOW[tone] }}>
        <path d="M12 3.2 19.4 7v10L12 20.8 4.6 17V7l7.4-3.8Z" fill="currentColor" opacity="0.12" stroke="currentColor" strokeWidth="1.6" />
        <circle cx="12" cy="12" r="1.95" fill="currentColor" opacity="0.95" />
        <path d="M12 5.6v3.4M12 15v3.4M5.6 12H9M15 12h3.4" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" opacity="0.92" />
      </svg>
    );
  }

  if (name === 'punitiveMeasure') {
    return (
      <svg className="chipIcon" viewBox="0 0 24 24" aria-hidden="true" focusable="false" style={{ filter: ICON_GLOW[tone] }}>
        <path d="M12 4 4.5 8.2V10h15V8.2L12 4Z" fill="currentColor" opacity="0.9" />
        <path d="M6.4 11.5h2v6h-2v-6Zm9.2 0h2v6h-2v-6Z" fill="currentColor" opacity="0.82" />
        <path d="M8.7 12.3 12 15l3.3-2.7" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" opacity="0.95" />
        <path d="M4.5 18.5h15" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" opacity="0.95" />
      </svg>
    );
  }

  if (name === 'unauthorizedPassage') {
    return (
      <svg className="chipIcon" viewBox="0 0 24 24" aria-hidden="true" focusable="false" style={{ filter: ICON_GLOW[tone] }}>
        <path d="M5.5 5.5h13v13h-13z" fill="none" stroke="currentColor" strokeWidth="1.8" opacity="0.92" />
        <path d="M8.2 5.5v13M12 5.5v13M15.8 5.5v13" fill="none" stroke="currentColor" strokeWidth="1.35" opacity="0.76" />
        <path d="M6.2 17.8 17.8 6.2" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" opacity="0.95" />
      </svg>
    );
  }

  if (name === 'momentum') {
    return (
      <svg className="chipIcon" viewBox="0 0 24 24" aria-hidden="true" focusable="false" style={{ filter: ICON_GLOW[tone] }}>
        <path d="M4.2 13.5c1.15-3.65 4.1-6.1 8.2-6.1 2.05 0 3.9.62 5.35 1.72" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
        <path d="M15.35 5.35 19.9 9.1l-5.8 1.25" fill="currentColor" opacity="0.92" />
        <path d="M6 18.35h8.4" stroke="currentColor" strokeWidth="2.15" strokeLinecap="round" opacity="0.95" />
        <path d="M11.1 15.25 14.4 18.35l-3.3 3.05" fill="currentColor" opacity="0.95" />
      </svg>
    );
  }

  if (name === 'causticCompound') {
    return (
      <svg className="chipIcon" viewBox="0 0 24 24" aria-hidden="true" focusable="false" style={{ filter: ICON_GLOW[tone] }}>
        <path d="M9 3.8h6v2.25c0 1.15-.38 2.2-1.02 3.05l-.98 1.27v.78c2.95.45 5.2 3 5.2 6.08 0 1.66-.46 2.9-1.32 3.57-.81.63-1.98.85-3.38.85H10.5c-1.4 0-2.57-.22-3.38-.85-.86-.67-1.32-1.91-1.32-3.57 0-3.08 2.25-5.63 5.2-6.08v-.78l-.98-1.27A5.03 5.03 0 0 1 9 6.05V3.8Z" fill="currentColor" opacity="0.9" />
        <path d="M9.1 16.2c1.1-1.25 1.85-1.7 2.55-1.7.92 0 1.32.62 1.83 1.38.47.68.94 1.37 1.88 1.37.44 0 .97-.15 1.63-.54v2.15c-.58.25-1.1.36-1.58.36-1.9 0-2.74-1.22-3.3-2.03-.23-.34-.4-.57-.52-.68-.28.1-.75.5-1.61 1.69L9.1 16.2Z" fill="#000" opacity="0.32" />
      </svg>
    );
  }

  if (name === 'mindrotCompound') {
    return (
      <svg className="chipIcon" viewBox="0 0 24 24" aria-hidden="true" focusable="false" style={{ filter: ICON_GLOW[tone] }}>
        <path d="M12 4.2c3.9 0 6.9 2.65 6.9 6.45 0 1.65-.58 3.1-1.58 4.2-.1 1.25.2 2.18.82 3.15.18.28.2.64.04.94-.16.3-.47.5-.81.5-1.43 0-2.62-.46-3.67-1.37-.57.15-1.16.23-1.7.23-3.9 0-6.9-2.64-6.9-6.45S8.1 4.2 12 4.2Z" fill="currentColor" opacity="0.9" />
        <circle cx="9.2" cy="10.35" r="1.05" fill="#000" opacity="0.34" />
        <circle cx="13.05" cy="9.55" r="0.95" fill="#000" opacity="0.3" />
        <circle cx="14.55" cy="12.55" r="1.1" fill="#000" opacity="0.34" />
      </svg>
    );
  }

  if (name === 'ironCompound') {
    return (
      <svg className="chipIcon" viewBox="0 0 24 24" aria-hidden="true" focusable="false" style={{ filter: ICON_GLOW[tone] }}>
        <path d="M12 3.1 19.2 6.8v5.2c0 4.3-2.44 7-7.2 8.9-4.76-1.9-7.2-4.6-7.2-8.9V6.8L12 3.1Z" fill="currentColor" opacity="0.9" />
        <path d="M8.2 12.15h7.6M12 8.2v7.9" stroke="#000" strokeWidth="1.8" strokeLinecap="round" opacity="0.26" />
      </svg>
    );
  }

  if (name === 'catalystCompound') {
    return (
      <svg className="chipIcon" viewBox="0 0 24 24" aria-hidden="true" focusable="false" style={{ filter: ICON_GLOW[tone] }}>
        <path d="M12 2.9 19.1 8l-2.72 10.15H7.62L4.9 8 12 2.9Z" fill="currentColor" opacity="0.9" />
        <path d="M12 6.3 9.15 11h2.05l-1.02 4.05 4.67-5.9h-2.1L12 6.3Z" fill="#000" opacity="0.28" />
      </svg>
    );
  }

  if (name === 'sedativeCompound') {
    return (
      <svg className="chipIcon" viewBox="0 0 24 24" aria-hidden="true" focusable="false" style={{ filter: ICON_GLOW[tone] }}>
        <path d="M12 3.4a6.4 6.4 0 1 1 0 12.8 6.4 6.4 0 0 1 0-12.8Z" fill="currentColor" opacity="0.9" />
        <path d="M12 7v5.3l3.2 2" stroke="#000" strokeWidth="1.9" strokeLinecap="round" opacity="0.3" />
      </svg>
    );
  }

  if (name === 'precision') {
    return (
      <svg className="chipIcon" viewBox="0 0 24 24" aria-hidden="true" focusable="false" style={{ filter: ICON_GLOW[tone] }}>
        <circle cx="12" cy="12" r="6.35" stroke="currentColor" strokeWidth="1.8" opacity="0.92" fill="none" />
        <circle cx="12" cy="12" r="2.15" fill="currentColor" opacity="0.95" />
        <path d="M12 3.3v3.1M12 17.6v3.1M3.3 12h3.1M17.6 12h3.1" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" opacity="0.92" />
      </svg>
    );
  }

  if (name === 'ambush') {
    return (
      <svg className="chipIcon" viewBox="0 0 24 24" aria-hidden="true" focusable="false" style={{ filter: ICON_GLOW[tone] }}>
        <path d="M4.9 19.1 12.2 4.3l6.9 6.9-14.2 7.9Z" fill="currentColor" opacity="0.9" />
        <path d="M13.1 6.2 17.2 10.3" stroke="#000" strokeWidth="1.6" strokeLinecap="round" opacity="0.28" />
        <path d="M5.2 18.8 3.6 20.4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" opacity="0.92" />
      </svg>
    );
  }

  if (name === 'roomLightDark') {
    return (
      <svg className="chipIcon" viewBox="0 0 24 24" aria-hidden="true" focusable="false" style={{ filter: ICON_GLOW[tone] }}>
        <circle cx="12" cy="12" r="7.2" fill="currentColor" opacity="0.18" stroke="currentColor" strokeWidth="1.5" />
        <path d="M14.8 6.2a6.6 6.6 0 1 0 0 11.6 5.6 5.6 0 1 1 0-11.6Z" fill="currentColor" opacity="0.92" />
      </svg>
    );
  }

  if (name === 'roomLightSteady') {
    return (
      <svg className="chipIcon" viewBox="0 0 24 24" aria-hidden="true" focusable="false" style={{ filter: ICON_GLOW[tone] }}>
        <circle cx="12" cy="12" r="6.1" fill="currentColor" opacity="0.92" />
        <path d="M12 2.8v2.6M12 18.6v2.6M2.8 12h2.6M18.6 12h2.6M5.3 5.3l1.85 1.85M16.85 16.85l1.85 1.85M18.7 5.3l-1.85 1.85M7.15 16.85 5.3 18.7" stroke="currentColor" strokeWidth="1.55" strokeLinecap="round" opacity="0.88" />
      </svg>
    );
  }

  if (name === 'roomLightBright') {
    return (
      <svg className="chipIcon" viewBox="0 0 24 24" aria-hidden="true" focusable="false" style={{ filter: ICON_GLOW[tone] }}>
        <circle cx="12" cy="12" r="5.4" fill="currentColor" opacity="0.95" />
        <path d="M12 1.9v3.1M12 19v3.1M1.9 12h3.1M19 12h3.1M4.5 4.5l2.2 2.2M17.3 17.3l2.2 2.2M19.5 4.5l-2.2 2.2M6.7 17.3l-2.2 2.2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" opacity="0.94" />
      </svg>
    );
  }

  if (name === 'pulseEngine') {
    return (
      <svg className="chipIcon" viewBox="0 0 24 24" aria-hidden="true" focusable="false" style={{ filter: ICON_GLOW[tone] }}>
        <path d="M12 2.8 19 6.6v6.2c0 4.35-2.5 7.1-7 8.45-4.5-1.35-7-4.1-7-8.45V6.6l7-3.8Z" fill="currentColor" opacity="0.15" stroke="currentColor" strokeWidth="1.6" />
        <path d="M8.25 12h7.5M12 8.25v7.5" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" opacity="0.94" />
      </svg>
    );
  }

  if (name === 'synapticRetort') {
    return (
      <svg className="chipIcon" viewBox="0 0 24 24" aria-hidden="true" focusable="false" style={{ filter: ICON_GLOW[tone] }}>
        <path d="M12 20.6c-5.6-3.55-8.45-6.55-8.45-10.25A4.8 4.8 0 0 1 12 7.4a4.8 4.8 0 0 1 8.45 2.95c0 3.7-2.85 6.7-8.45 10.25Z" fill="currentColor" opacity="0.18" stroke="currentColor" strokeWidth="1.55" />
        <path d="M12 6.1v4.1M12 6.1l-2 2.05M12 6.1l2 2.05" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" opacity="0.95" />
      </svg>
    );
  }

  if (name === 'astralReliquary') {
    return (
      <svg className="chipIcon" viewBox="0 0 24 24" aria-hidden="true" focusable="false" style={{ filter: ICON_GLOW[tone] }}>
        <path d="M8 3.8h8M8 20.2h8" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" opacity="0.92" />
        <path d="M9 5.2h6v2.1c0 1.4-.6 2.75-1.65 3.7L12 12.25l-1.35-1.25A5.28 5.28 0 0 1 9 7.3V5.2Zm0 13.6h6v-2.1c0-1.4-.6-2.75-1.65-3.7L12 11.75l-1.35 1.25A5.28 5.28 0 0 0 9 16.7v2.1Z" fill="currentColor" opacity="0.88" />
      </svg>
    );
  }

  if (name === 'primeCrucible') {
    return (
      <svg className="chipIcon" viewBox="0 0 24 24" aria-hidden="true" focusable="false" style={{ filter: ICON_GLOW[tone] }}>
        <path d="M12 3.2 17.8 8 12 20.8 6.2 8 12 3.2Z" fill="currentColor" opacity="0.18" stroke="currentColor" strokeWidth="1.5" />
        <path d="M12 7.1c1.9 1.55 2.85 3 2.85 4.45 0 1.95-1.2 3.35-2.85 4.75-1.65-1.4-2.85-2.8-2.85-4.75 0-1.45.95-2.9 2.85-4.45Z" fill="currentColor" opacity="0.92" />
      </svg>
    );
  }

  const src =
    name === 'reflect'
      ? reflectIcon
      : name === 'negate'
        ? negateIcon
        : name === 'stun'
          ? stunIcon
          : name === 'discard'
            ? discardIcon
            : name === 'chain'
              ? chainIcon
              : name === 'delayed'
                ? delayedIcon
                : name === 'hp'
                  ? hpIcon
                  : spIcon;

  // PNG icons (white, grungy). Keep the same className so existing sizing CSS continues to work.
  // We add a subtle colored glow when needed so the chip reads clearly.
  return <img className="chipIcon" src={src} alt="" aria-hidden="true" draggable={false} style={{ filter: ICON_GLOW[tone] }} />;
}

function Chip({
  chipKey,
  title,
  tone = 'neutral',
  className,
  children,
  pulseTick,
}: {
  chipKey: string;
  title: string;
  tone?: ChipTone;
  className?: string;
  children: React.ReactNode;
  pulseTick?: number;
}) {
  const ctx = React.useContext(TooltipCtx);
  const ref = React.useRef<HTMLSpanElement | null>(null);
  const [isPulsing, setIsPulsing] = React.useState(false);

  React.useEffect(() => {
    if (pulseTick == null) return;
    setIsPulsing(false);
    const raf = requestAnimationFrame(() => setIsPulsing(true));
    return () => cancelAnimationFrame(raf);
  }, [pulseTick]);

  const isPinned = !!ctx && ctx.isMobile && ctx.pinnedKey === chipKey;

  const onMouseEnter = () => {
    if (!ctx || ctx.isMobile) return;
    ctx.show(chipKey, title, ref.current);
  };

  const onMouseLeave = () => {
    if (!ctx || ctx.isMobile) return;
    ctx.hide(chipKey);
  };

  const onFocus = () => {
    if (!ctx || ctx.isMobile) return;
    ctx.show(chipKey, title, ref.current);
  };

  const onBlur = () => {
    if (!ctx) return;
    if (ctx.isMobile) return;
    ctx.hide(chipKey);
  };

  const onPointerDown: React.PointerEventHandler<HTMLSpanElement> = (e) => {
    if (!ctx || !ctx.isMobile) return;
    if (e.pointerType !== 'touch') return;
    e.preventDefault();
    e.stopPropagation();
    ctx.toggle(chipKey, title, ref.current);
  };

  const onClick: React.MouseEventHandler<HTMLSpanElement> = (e) => {
    // Some mobile browsers still dispatch click after touch.
    if (!ctx || !ctx.isMobile) return;
    e.preventDefault();
    e.stopPropagation();
    ctx.toggle(chipKey, title, ref.current);
  };

  const onKeyDown: React.KeyboardEventHandler<HTMLSpanElement> = (e) => {
    if (e.key === 'Escape') {
      ctx?.hide(chipKey);
      return;
    }

    // Space/Enter toggles on mobile (and still works on desktop if you want to pin).
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      ctx?.toggle(chipKey, title, ref.current);
    }
  };

  return (
    <span
      ref={ref}
      className={[isPulsing ? 'chip chipHasTooltip chipPulse' : 'chip chipHasTooltip', className].filter(Boolean).join(' ')}
      data-status-chip-key={chipKey}
      data-tooltip-open={isPinned ? 'true' : 'false'}
      aria-label={title}
      aria-haspopup="true"
      aria-expanded={isPinned}
      tabIndex={0}
      role="button"
      style={TONE_STYLES[tone]}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      onFocus={onFocus}
      onBlur={onBlur}
      onPointerDown={onPointerDown}
      onClick={onClick}
      onKeyDown={onKeyDown}
      onAnimationEnd={() => { if (isPulsing) setIsPulsing(false); }}
    >
      {children}
    </span>
  );
}

function formatMult(n: number): string {
  if (!Number.isFinite(n)) return 'x?';
  const rounded = Math.round(n * 100) / 100;
  const isInt = Math.abs(rounded - Math.round(rounded)) < 1e-9;
  return `x${isInt ? String(Math.round(rounded)) : String(rounded)}`;
}

function renderInline(items: ChipItem[]) {
  return (
    <div className="statusChips">
      {items.map((it) => (
        <React.Fragment key={it.key}>{it.node}</React.Fragment>
      ))}
    </div>
  );
}

function renderArc(items: ChipItem[], variant: 'default' | 'player' = 'default') {
  // The arc layout is meant for a small number of chips.
  // If we ever have a lot of concurrent statuses, fall back to the standard wrap.
  if (items.length > 7) return renderInline(items);

  const n = items.length;
  const compactLevel = variant === 'player' ? Math.max(0, n - 4) : 0;

  // Center the group on the arc.
  // For small counts, keep the spread tight so chips sit around the center (instead of at the far ends).
  const step = 30;
  const maxSpread = compactLevel > 0 ? 104 : 120;
  const spread = n <= 1 ? 0 : Math.min(maxSpread, step * (n - 1));
  const startDeg = -spread / 2;
  const stepDeg = n <= 1 ? 0 : spread / (n - 1);

  // Larger radius to accommodate bigger, circular chips.
  // The wrapper scales with the combat UI, so these px values stay visually consistent.
  const radiusBase = 118 + Math.min(44, n * 6);
  const radius = compactLevel > 0 ? Math.max(122, radiusBase - compactLevel * 16) : radiusBase;

  const arcStyle: React.CSSProperties | undefined =
    compactLevel > 0
      ? {
          ['--player-status-chip-scale' as any]: String(Math.max(0.68, 0.9 - compactLevel * 0.08)),
          ['--player-status-arc-top' as any]: compactLevel >= 3 ? '48%' : compactLevel === 2 ? '50%' : '52%',
        }
      : undefined;

  return (
    <div className="statusChips statusChipsArc" style={arcStyle}>
      {items.map((it, i) => {
        const deg = n <= 1 ? 0 : startDeg + stepDeg * i;
        const rad = (deg * Math.PI) / 180;

        // Upward arc (highest chip at the center).
        const x = Math.round(Math.sin(rad) * radius);
        const y = Math.round(-Math.cos(rad) * radius);

        return (
          <div
            key={it.key}
            className="chipArcItem"
            style={{
              transform: `translate(-50%, -50%) translate(${x}px, ${y}px)`,
            }}
          >
            {it.node}
          </div>
        );
      })}
    </div>
  );
}

function StatusChips({
  playerStatus = EMPTY_PLAYER_STATUS,
  enemyStatus = EMPTY_ENEMY_STATUS,
  scope = 'all',
  layout = 'inline',
  pulseMap = {},
}: StatusChipsProps) {
  // Use a fixed-position portal tooltip so the chip text is never clipped by
  // combat layout containers with overflow constraints.
  const SHOW_PORTAL_TOOLTIPS = true;

  const [isMobile, setIsMobile] = React.useState<boolean>(false);
  const [tooltip, setTooltip] = React.useState<TooltipState | null>(null);
  const [pinnedKey, setPinnedKey] = React.useState<string | null>(null);

  // Determine mobile layout based on viewport width. Update on resize.
  React.useEffect(() => {
    const update = () => {
      if (typeof window === 'undefined') return;
      setIsMobile(window.innerWidth <= 768);
    };
    update();
    window.addEventListener('resize', update);
    return () => window.removeEventListener('resize', update);
  }, []);

  // If we move from mobile to desktop, clear any pinned tooltip state.
  React.useEffect(() => {
    if (!isMobile) {
      setPinnedKey(null);
    }
  }, [isMobile]);

  // On mobile, tapping elsewhere closes the pinned tooltip.
  React.useEffect(() => {
    if (!isMobile || !pinnedKey) return;
    const close = () => {
      setPinnedKey(null);
      setTooltip(null);
    };
    window.addEventListener('pointerdown', close);
    window.addEventListener('resize', close);
    return () => {
      window.removeEventListener('pointerdown', close);
      window.removeEventListener('resize', close);
    };
  }, [isMobile, pinnedKey]);

  const show = React.useCallback(
    (key: string, text: string, el: HTMLElement | null) => {
      if (isMobile || !SHOW_PORTAL_TOOLTIPS) return;
      const next = computeTooltipState(key, text, el);
      if (next) setTooltip(next);
    },
    [isMobile, SHOW_PORTAL_TOOLTIPS]
  );

  const hide = React.useCallback(
    (key: string) => {
      if (isMobile) {
        setPinnedKey((cur) => (cur === key ? null : cur));
        setTooltip((cur) => (cur && cur.key === key ? null : cur));
        return;
      }
      if (!SHOW_PORTAL_TOOLTIPS) return;
      setTooltip((cur) => (cur && cur.key === key ? null : cur));
    },
    [isMobile, SHOW_PORTAL_TOOLTIPS]
  );

  const toggle = React.useCallback(
    (key: string, text: string, el: HTMLElement | null) => {
      const next = computeTooltipState(key, text, el);
      if (!next) {
        setPinnedKey(null);
        setTooltip(null);
        return;
      }

      // Mobile: tap-to-pin (tap again to close).
      if (isMobile) {
        setPinnedKey((cur) => {
          const nextPinned = cur === key ? null : key;
          setTooltip(nextPinned ? next : null);
          return nextPinned;
        });
        return;
      }

      // Desktop: allow keyboard toggle (Enter/Space) to show/hide.
      if (!SHOW_PORTAL_TOOLTIPS) return;
      setTooltip((cur) => (cur && cur.key === key ? null : next));
    },
    [isMobile, SHOW_PORTAL_TOOLTIPS]
  );

  const controller = React.useMemo<TooltipController>(
    () => ({ isMobile, pinnedKey, show, hide, toggle }),
    [isMobile, pinnedKey, show, hide, toggle]
  );

  const tooltipPortal =
    SHOW_PORTAL_TOOLTIPS && tooltip && typeof document !== 'undefined'
      ? createPortal(
          <div
            className={`statusChipTooltip statusChipTooltip--${tooltip.placement}`}
            style={{ left: tooltip.x, top: tooltip.y }}
            role="tooltip"
            aria-hidden="false"
          >
            {tooltip.text}
          </div>,
          document.body
        )
      : null;

  const thresholdVerifierUi = useGameStore(
    React.useCallback((store) => {
      const state: any = store.state ?? {};
      const locks = Array.isArray(state.unauthorizedPassageLocks) ? state.unauthorizedPassageLocks : [];
      const normalizedLocks = locks
        .map((entry: any) => ({
          cardId: String(entry?.cardId ?? '').trim(),
          turns: Math.max(0, Number(entry?.turns ?? 0) || 0),
        }))
        .filter((entry: any) => entry.cardId.length > 0 && entry.turns > 0);
      return {
        punitiveMeasureTurns: Math.max(0, Number(state.punitiveMeasureTurns ?? 0) || 0),
        unauthorizedLockedCount: normalizedLocks.length,
        unauthorizedMaxTurns: normalizedLocks.reduce((max: number, entry: any) => Math.max(max, entry.turns), 0),
      };
    }, []),
    shallow,
  );

  const blackhandUi = useGameStore(
    React.useCallback((store) => {
      const state: any = store.state ?? {};
      const turn = Number(state.turn ?? 0) || 0;
      const isPlayerTurn = state.phase === 'player';
      const pressurePointActive = isPlayerTurn && Number(state.blackhandPressurePointTurn ?? -1) === turn;
      return {
        playerMarked: !!state.playerMarkedByBlackhand,
        shadowstepActive: isPlayerTurn && Number(state.blackhandShadowstepTurn ?? -1) === turn,
        pressurePointActive,
        pressurePointMarked: pressurePointActive && !!state.playerMarkedByBlackhand,
        enemyBonusActionsNextTurn: Math.max(0, Number(state.enemyBonusActionsNextTurn ?? 0) || 0),
        enemyId: String(state.enemyId ?? ''),
      };
    }, []),
    shallow,
  );

  const player: ChipItem[] = [];
  const enemy: ChipItem[] = [];

  // ----------------------------
  // Player status (defensive)
  // ----------------------------
  if (thresholdVerifierUi.punitiveMeasureTurns > 0) {
    player.push({
      key: 'thresholdVerifierPunitiveMeasure',
      node: (
        <Chip
          chipKey="thresholdVerifierPunitiveMeasure"
          tone="debuff"
          className="chip--thresholdVerifier chip--punitiveMeasure"
          title={`Punitive Measure: for ${thresholdVerifierUi.punitiveMeasureTurns} more turn(s), lose 5 HP whenever you play a black card.`}
        >
          <Icon name="punitiveMeasure" tone="debuff" />
          <span className="chipValue">-5 HP</span>
          <span className="chipSub">BLK</span>
        </Chip>
      ),
    });
  }

  if (thresholdVerifierUi.unauthorizedLockedCount > 0) {
    player.push({
      key: 'thresholdVerifierUnauthorizedPassage',
      node: (
        <Chip
          chipKey="thresholdVerifierUnauthorizedPassage"
          tone="debuff"
          className="chip--thresholdVerifier chip--unauthorizedPassage"
          title={`Unauthorized Passage: ${thresholdVerifierUi.unauthorizedLockedCount} card(s) cannot be played. Longest remaining duration: ${thresholdVerifierUi.unauthorizedMaxTurns} turn(s).`}
        >
          <Icon name="unauthorizedPassage" tone="debuff" />
          <span className="chipValue">{thresholdVerifierUi.unauthorizedLockedCount}</span>
          <span className="chipSub">LOCK</span>
        </Chip>
      ),
    });
  }

  if ((playerStatus.custodianForfeitureTurns ?? 0) > 0) {
    player.push({
      key: 'custodianForfeiture',
      node: (
        <Chip
          chipKey="custodianForfeiture"
          tone="debuff"
          title={`Forfeiture: for ${playerStatus.custodianForfeitureTurns} more turn(s), player cards discarded by effects, discard costs, or forced discard are exiled instead.`}
        >
          <Icon name="discard" tone="debuff" />
          <span className="chipValue">{playerStatus.custodianForfeitureTurns}</span>
          <span className="chipSub">FORF</span>
        </Chip>
      ),
    });
  }

  if (playerStatus.reflectPct > 0) {
    player.push({
      key: 'reflect',
      node: (
        <Chip chipKey="reflect" pulseTick={pulseMap.reflect} title={`Reflect ${playerStatus.reflectPct}% of next damage`}>
          <Icon name="reflect" />
          <span className="chipValue">{playerStatus.reflectPct}%</span>
        </Chip>
      ),
    });
  }

  if (playerStatus.negateNextDamage > 0) {
    player.push({
      key: 'negate',
      node: (
        <Chip chipKey="negate" pulseTick={pulseMap.negate} title={`Next damage reduced to 0 (${playerStatus.negateNextDamage})`}>
          <Icon name="negate" />
          <span className="chipValue">{playerStatus.negateNextDamage}</span>
        </Chip>
      ),
    });
  }

  // ----------------------------
  // Player status (offense / economy)
  // ----------------------------
  if (playerStatus.nextAttackBonus > 0) {
    player.push({
      key: 'atkBonus',
      node: (
        <Chip chipKey="atkBonus" pulseTick={pulseMap.atkBonus} title={`Next Attack-tagged card: +${playerStatus.nextAttackBonus} damage`}>
          <Icon name="attackBonus" />
          <span className="chipValue">+{playerStatus.nextAttackBonus}</span>
        </Chip>
      ),
    });
  }

  if (playerStatus.stunned > 0) {
    const t = Math.max(0, Number(playerStatus.stunned) || 0);
    player.push({
      key: 'stun',
      node: (
        <Chip chipKey="stun" tone="debuff" title={`Stunned for ${t} turn(s): you cannot play cards this turn, but you can still Focus.`}>
          <Icon name="stun" tone="debuff" />
          <span className="chipValue">{t}</span>
        </Chip>
      ),
    });
  }

  if (playerStatus.nextCardCost0 > 0) {
    const n = Math.max(0, Number(playerStatus.nextCardCost0) || 0);
    player.push({
      key: 'free',
      node: (
        <Chip chipKey="free" pulseTick={pulseMap.free} title={`Next card costs 0 (HP + SP)${n > 1 ? ` (x${n})` : ''}`}>
          <Icon name="free" />
          <span className="chipValue">0</span>
          {n > 1 ? <span className="chipSub">×{n}</span> : null}
        </Chip>
      ),
    });
  }

  if (playerStatus.playerDamageMultThisTurn > 1) {
    const m = Number(playerStatus.playerDamageMultThisTurn) || 1;
    player.push({
      key: 'dmgUp',
      node: (
        <Chip chipKey="dmgUp" pulseTick={pulseMap.dmgUp} title={`Damage dealt this turn: ${formatMult(m)}`}>
          <Icon name="multUp" />
          <span className="chipValue">{formatMult(m)}</span>
        </Chip>
      ),
    });
  }

  if (playerStatus.playerIncomingDamageMultNextTurn > 1) {
    const m = Number(playerStatus.playerIncomingDamageMultNextTurn) || 1;
    player.push({
      key: 'dmgDown',
      node: (
        <Chip chipKey="dmgDown" pulseTick={pulseMap.dmgDown} tone="debuff" title={`Debuff: damage taken next enemy turn: ${formatMult(m)}`}>
          <Icon name="multDown" tone="debuff" />
          <span className="chipValue">{formatMult(m)}</span>
        </Chip>
      ),
    });
  }

  // ----------------------------
  // Player debuffs (Hexbinder)
  // ----------------------------
  if (playerStatus.nextMagicCardCostDoubleSP > 0) {
    const n = Math.max(0, Number(playerStatus.nextMagicCardCostDoubleSP) || 0);
    player.push({
      key: 'magicDouble',
      node: (
        <Chip chipKey="magicDouble" pulseTick={pulseMap.magicDouble} tone="debuff" title={`Debuff: your next Magic card costs double SP${n > 1 ? ` (x${n})` : ''}`}>
          <Icon name="curse" tone="debuff" />
          <span className="chipValue">x2</span>
          {n > 1 ? <span className="chipSub">×{n}</span> : null}
        </Chip>
      ),
    });
  }

  if (playerStatus.playerWeakenedTurns > 0) {
    const t = Math.max(0, Number(playerStatus.playerWeakenedTurns) || 0);
    player.push({
      key: 'weaken',
      node: (
        <Chip chipKey="weaken" pulseTick={pulseMap.weaken} tone="debuff" title={`Debuff: your card effects are weakened for ${t} turn(s)`}>
          <Icon name="weaken" tone="debuff" />
          <span className="chipValue">{t}</span>
        </Chip>
      ),
    });
  }


  if (playerStatus.activeCompound === 'caustic') {
    player.push({
      key: 'compoundCaustic',
      node: (
        <Chip
          chipKey="compoundCaustic"
          tone="debuff"
          className="chip--compound chip--compoundCaustic"
          title="Caustic Compound: at the start of your turn, take 4 damage."
        >
          <Icon name="causticCompound" tone="debuff" />
          <span className="chipValue">4</span>
          <span className="chipSub">HP</span>
        </Chip>
      ),
    });
  }

  if (playerStatus.activeCompound === 'mindrot') {
    player.push({
      key: 'compoundMindrot',
      node: (
        <Chip
          chipKey="compoundMindrot"
          tone="debuff"
          className="chip--compound chip--compoundMindrot"
          title="Mindrot Compound: at the start of your turn, burn 6 SP."
        >
          <Icon name="mindrotCompound" tone="debuff" />
          <span className="chipValue">6</span>
          <span className="chipSub">SP</span>
        </Chip>
      ),
    });
  }

  if (playerStatus.activeCompound === 'sedative') {
    player.push({
      key: 'compoundSedative',
      node: (
        <Chip
          chipKey="compoundSedative"
          tone="debuff"
          className="chip--compound chip--compoundSedative"
          title="Sedative Compound: the first player card you play each turn gains Delay (1)."
        >
          <Icon name="sedativeCompound" tone="debuff" />
          <span className="chipValue">DLY</span>
          <span className="chipSub">+1</span>
        </Chip>
      ),
    });
  }

  if (playerStatus.darkApostleCursedFateActive) {
    player.push({
      key: 'darkApostleCursedFate',
      node: (
        <Chip
          chipKey="darkApostleCursedFate"
          tone="debuff"
          title="Debuff: if you end your turn without attacking, Cursed Fate deals 20 damage and burns 20 SP. Ends when you attack."
        >
          <Icon name="curse" tone="debuff" />
          <span className="chipValue">20</span>
          <span className="chipSub">HP/SP</span>
        </Chip>
      ),
    });
  }

  // ----------------------------
  // Enemy status
  // ----------------------------
  if (enemyStatus.stunned > 0) {
    enemy.push({
      key: 'enemyStun',
      node: (
        <Chip chipKey="enemyStun" pulseTick={pulseMap.enemyStun} tone="enemy" title={`Enemy stunned for ${enemyStatus.stunned} turn(s)`}>
          <Icon name="stun" tone="enemy" />
          <span className="chipValue">{enemyStatus.stunned}</span>
        </Chip>
      ),
    });
  }

  if ((enemyStatus.enemyDamageMultTurns ?? 0) > 0 && Number(enemyStatus.enemyDamageMult ?? 1) < 1) {
    const t = Math.max(0, Number(enemyStatus.enemyDamageMultTurns ?? 0) || 0);
    const m = Number(enemyStatus.enemyDamageMult ?? 1) || 1;
    enemy.push({
      key: 'enemyWeaken',
      node: (
        <Chip chipKey="enemyWeaken" pulseTick={pulseMap.enemyWeaken} tone="enemy" title={`Enemy damage reduced to ${formatMult(m)} for ${t} turn(s)`}>
          <Icon name="weaken" tone="enemy" />
          <span className="chipValue">{formatMult(m)}</span>
          <span className="chipSub">{t}</span>
        </Chip>
      ),
    });
  }

  if (enemyStatus.mercenaryPrecision) {
    enemy.push({
      key: 'enemyMercenaryPrecision',
      node: (
        <Chip
          chipKey="enemyMercenaryPrecision"
          tone="enemy"
          title="Precision: the next Deadeye Shot deals 36 damage instead of 18, ignores Reflect, then removes Precision."
        >
          <Icon name="precision" tone="enemy" />
          <span className="chipValue">36</span>
          <span className="chipSub">SHOT</span>
        </Chip>
      ),
    });
  }

  if (enemyStatus.mercenaryAmbush) {
    enemy.push({
      key: 'enemyMercenaryAmbush',
      node: (
        <Chip
          chipKey="enemyMercenaryAmbush"
          tone="enemy"
          title="Ambush: the next Gutting Strike deals 34 damage instead of 16, ignores Armor, then removes Ambush."
        >
          <Icon name="ambush" tone="enemy" />
          <span className="chipValue">34</span>
          <span className="chipSub">AMB</span>
        </Chip>
      ),
    });
  }


  if (enemyStatus.lampKeeperRoomLight === 'dark') {
    enemy.push({
      key: 'enemyLampKeeperRoomLightDark',
      node: (
        <Chip
          chipKey="enemyLampKeeperRoomLightDark"
          tone="enemy"
          title="Room Light: Dark. Lamp Keeper's dark-state effects are active. If combat ends now, exploration torchlight is set to 20% of max."
        >
          <Icon name="roomLightDark" tone="enemy" />
          <span className="chipValue">DARK</span>
          <span className="chipSub">LIGHT</span>
        </Chip>
      ),
    });
  }

  if (enemyStatus.lampKeeperRoomLight === 'steady') {
    enemy.push({
      key: 'enemyLampKeeperRoomLightSteady',
      node: (
        <Chip
          chipKey="enemyLampKeeperRoomLightSteady"
          tone="enemy"
          title="Room Light: Steady. Lamp Keeper's steady-state effects are active. If combat ends now, exploration torchlight is set to 50% of max."
        >
          <Icon name="roomLightSteady" tone="enemy" />
          <span className="chipValue">STDY</span>
          <span className="chipSub">LIGHT</span>
        </Chip>
      ),
    });
  }

  if (enemyStatus.lampKeeperRoomLight === 'bright') {
    enemy.push({
      key: 'enemyLampKeeperRoomLightBright',
      node: (
        <Chip
          chipKey="enemyLampKeeperRoomLightBright"
          tone="enemy"
          title="Room Light: Bright. Lamp Keeper's bright-state effects are active. If combat ends now, exploration torchlight is set to 80% of max."
        >
          <Icon name="roomLightBright" tone="enemy" />
          <span className="chipValue">BRT</span>
          <span className="chipSub">LIGHT</span>
        </Chip>
      ),
    });
  }

if (enemyStatus.lampKeeperDisposition === 'night_watch') {
  enemy.push({
    key: 'enemyLampKeeperNightWatch',
    node: (
      <Chip
        chipKey="enemyLampKeeperNightWatch"
        tone="enemy"
        title="Night Watch: the first time Room Light is lowered each round, Lamp Keeper gains 12 Armor."
      >
        <Icon name="reflect" tone="enemy" />
        <span className="chipValue">12</span>
        <span className="chipSub">NW</span>
      </Chip>
    ),
  });
}

if (enemyStatus.lampKeeperDisposition === 'tended_flame') {
  enemy.push({
    key: 'enemyLampKeeperTendedFlame',
    node: (
      <Chip
        chipKey="enemyLampKeeperTendedFlame"
        tone="enemy"
        title="Tended Flame: whenever Room Light changes, burn 5 SP."
      >
        <Icon name="sp" tone="enemy" />
        <span className="chipValue">5</span>
        <span className="chipSub">TF</span>
      </Chip>
    ),
  });
}

if (enemyStatus.lampKeeperDisposition === 'open_flame') {
  enemy.push({
    key: 'enemyLampKeeperOpenFlame',
    node: (
      <Chip
        chipKey="enemyLampKeeperOpenFlame"
        tone="enemy"
        title="Open Flame: the first time Room Light is raised each round, deal 6 damage."
      >
        <Icon name="attackBonus" tone="enemy" />
        <span className="chipValue">6</span>
        <span className="chipSub">OF</span>
      </Chip>
    ),
  });
}



if (enemyStatus.isCustodian || (enemyStatus.custodianExiledAttackCount ?? 0) > 0) {
  enemy.push({
    key: 'enemyCustodianExiledAttacks',
    node: (
      <Chip
        chipKey="enemyCustodianExiledAttacks"
        tone="enemy"
        title={`Exiled Attacks: ${enemyStatus.custodianExiledAttackCount}. Reissued Violence deals 9 damage for each.`}
      >
        <Icon name="attackBonus" tone="enemy" />
        <span className="chipValue">{enemyStatus.custodianExiledAttackCount}</span>
        <span className="chipSub">ATK X</span>
      </Chip>
    ),
  });
}

if (enemyStatus.isCustodian || (enemyStatus.custodianExiledDefenseCount ?? 0) > 0) {
  enemy.push({
    key: 'enemyCustodianExiledDefenses',
    node: (
      <Chip
        chipKey="enemyCustodianExiledDefenses"
        tone="enemy"
        title={`Exiled Defenses: ${enemyStatus.custodianExiledDefenseCount}. Salvaged Ward gains 6 Armor for each.`}
      >
        <Icon name="reflect" tone="enemy" />
        <span className="chipValue">{enemyStatus.custodianExiledDefenseCount}</span>
        <span className="chipSub">DEF X</span>
      </Chip>
    ),
  });
}

if (enemyStatus.isCustodian || (enemyStatus.custodianExiledUtilityCount ?? 0) > 0) {
  enemy.push({
    key: 'enemyCustodianExiledUtilities',
    node: (
      <Chip
        chipKey="enemyCustodianExiledUtilities"
        tone="enemy"
        title={`Exiled Utilities: ${enemyStatus.custodianExiledUtilityCount}. Reprocessed Essence heals 3 HP for each.`}
      >
        <Icon name="hp" tone="enemy" />
        <span className="chipValue">{enemyStatus.custodianExiledUtilityCount}</span>
        <span className="chipSub">UTL X</span>
      </Chip>
    ),
  });
}

  if ((enemyStatus.reflectPct ?? 0) > 0) {
    enemy.push({
      key: 'enemyReflect',
      node: (
        <Chip
          chipKey="enemyReflect"
          tone="enemy"
          title={`Enemy Reflect ${enemyStatus.reflectPct}% of next damage`}
        >
          <Icon name="reflect" tone="enemy" />
          <span className="chipValue">{enemyStatus.reflectPct}%</span>
        </Chip>
      ),
    });
  }



  if ((enemyStatus.thresholdVerifierDeferredSanctionCount ?? 0) > 0) {
    const count = Math.max(0, Number(enemyStatus.thresholdVerifierDeferredSanctionCount ?? 0) || 0);
    const minTurns = Math.max(0, Number(enemyStatus.thresholdVerifierDeferredSanctionMinTurns ?? 0) || 0);
    enemy.push({
      key: 'thresholdVerifierDeferredSanction',
      node: (
        <Chip
          chipKey="thresholdVerifierDeferredSanction"
          tone="enemy"
          title={`Deferred Sanction: ${count} delayed bundle(s) pending. Next trigger in ${minTurns} turn(s).`}
        >
          <Icon name="delayed" tone="enemy" />
          <span className="chipValue">{count}</span>
          <span className="chipSub">DLY {minTurns}</span>
        </Chip>
      ),
    });
  }

  if (enemyStatus.activeCompound === 'caustic') {
    enemy.push({
      key: 'enemyCompoundCaustic',
      node: (
        <Chip
          chipKey="enemyCompoundCaustic"
          tone="enemy"
          className="chip--compound chip--compoundCaustic"
          title="Caustic Compound: at the start of the player's turn, deal 4 damage."
        >
          <Icon name="causticCompound" tone="enemy" />
          <span className="chipValue">4</span>
          <span className="chipSub">HP</span>
        </Chip>
      ),
    });
  }

  if (enemyStatus.activeCompound === 'mindrot') {
    enemy.push({
      key: 'enemyCompoundMindrot',
      node: (
        <Chip
          chipKey="enemyCompoundMindrot"
          tone="enemy"
          className="chip--compound chip--compoundMindrot"
          title="Mindrot Compound: at the start of the player's turn, burn 6 SP."
        >
          <Icon name="mindrotCompound" tone="enemy" />
          <span className="chipValue">6</span>
          <span className="chipSub">SP</span>
        </Chip>
      ),
    });
  }

  if (enemyStatus.activeCompound === 'iron') {
    enemy.push({
      key: 'enemyCompoundIron',
      node: (
        <Chip
          chipKey="enemyCompoundIron"
          tone="enemy"
          className="chip--compound chip--compoundIron"
          title="Iron Compound: this enemy takes 25% less damage."
        >
          <Icon name="ironCompound" tone="enemy" />
          <span className="chipValue">DR</span>
          <span className="chipSub">25%</span>
        </Chip>
      ),
    });
  }

  if (enemyStatus.activeCompound === 'catalyst') {
    enemy.push({
      key: 'enemyCompoundCatalyst',
      node: (
        <Chip
          chipKey="enemyCompoundCatalyst"
          tone="enemy"
          className="chip--compound chip--compoundCatalyst"
          title="Catalyst Compound: this enemy deals 30% more damage."
        >
          <Icon name="catalystCompound" tone="enemy" />
          <span className="chipValue">DMG</span>
          <span className="chipSub">+30%</span>
        </Chip>
      ),
    });
  }

  if (enemyStatus.activeCompound === 'sedative') {
    enemy.push({
      key: 'enemyCompoundSedative',
      node: (
        <Chip
          chipKey="enemyCompoundSedative"
          tone="enemy"
          className="chip--compound chip--compoundSedative"
          title="Sedative Compound: the first player card each turn gains Delay (1)."
        >
          <Icon name="sedativeCompound" tone="enemy" />
          <span className="chipValue">DLY</span>
          <span className="chipSub">+1</span>
        </Chip>
      ),
    });
  }

  if (enemyStatus.isPrimeVessel && enemyStatus.pulseEngineActive) {
    enemy.push({
      key: 'primeVesselPulseEngine',
      node: (
        <Chip
          chipKey="primeVesselPulseEngine"
          tone="enemy"
          className="chip--primeVessel chip--pulseEngine"
          title="Pulse Engine: This enemy has 30% damage reduction."
        >
          <Icon name="pulseEngine" tone="enemy" />
          <span className="chipValue">DR</span>
          <span className="chipSub">30%</span>
        </Chip>
      ),
    });
  }

  if (enemyStatus.isPrimeVessel && enemyStatus.synapticRetortActive) {
    enemy.push({
      key: 'primeVesselSynapticRetort',
      node: (
        <Chip
          chipKey="primeVesselSynapticRetort"
          tone="enemy"
          className="chip--primeVessel chip--synapticRetort"
          title="Synaptic Retort: When this enemy dies, it comes back once with 30% of its starting HP."
        >
          <Icon name="synapticRetort" tone="enemy" />
          <span className="chipValue">REV</span>
          <span className="chipSub">30%</span>
        </Chip>
      ),
    });
  }

  if (enemyStatus.isPrimeVessel && enemyStatus.astralReliquaryActive) {
    enemy.push({
      key: 'primeVesselAstralReliquary',
      node: (
        <Chip
          chipKey="primeVesselAstralReliquary"
          tone="enemy"
          className="chip--primeVessel chip--astralReliquary"
          title="Astral Reliquary: Delay cards played by this enemy resolve immediately."
        >
          <Icon name="astralReliquary" tone="enemy" />
          <span className="chipValue">NO</span>
          <span className="chipSub">DELAY</span>
        </Chip>
      ),
    });
  }

  if (enemyStatus.isPrimeVessel && enemyStatus.primeCrucibleActive) {
    enemy.push({
      key: 'primeVesselPrimeCrucible',
      node: (
        <Chip
          chipKey="primeVesselPrimeCrucible"
          tone="enemy"
          className="chip--primeVessel chip--primeCrucible"
          title="Prime Crucible: This enemy deals 30% more damage."
        >
          <Icon name="primeCrucible" tone="enemy" />
          <span className="chipValue">DMG</span>
          <span className="chipSub">+30%</span>
        </Chip>
      ),
    });
  }

  if (blackhandUi.enemyId === 'blackhand' && blackhandUi.enemyBonusActionsNextTurn > 0) {
    const totalNextActions = 1 + blackhandUi.enemyBonusActionsNextTurn;
    enemy.push({
      key: 'blackhandMomentum',
      node: (
        <Chip
          chipKey="blackhandMomentum"
          tone="enemy"
          className="chip--blackhand chip--momentum"
          title={`Momentum: The Blackhand will take ${totalNextActions} action(s) on his next turn.`}
        >
          <Icon name="momentum" tone="enemy" />
          <span className="chipValue">{totalNextActions}</span>
          <span className="chipSub">NEXT</span>
        </Chip>
      ),
    });
  }

  if (blackhandUi.playerMarked) {
    player.push({
      key: 'blackhandMarked',
      node: (
        <Chip
          chipKey="blackhandMarked"
          tone="debuff"
          className="chip--blackhand chip--marked"
          title="Marked: The Blackhand has designated you as a priority target. Execute only doubles if this Mark was already on you before the current turn."
        >
          <Icon name="mark" tone="debuff" />
          <span className="chipValue">MARK</span>
        </Chip>
      ),
    });
  }

  if (blackhandUi.shadowstepActive) {
    player.push({
      key: 'blackhandShadowstep',
      node: (
        <Chip
          chipKey="blackhandShadowstep"
          tone="debuff"
          className="chip--blackhand chip--shadowstep"
          title="Shadowstep: the first Attack card you play this turn will be evaded."
        >
          <Icon name="shadowstep" tone="debuff" />
          <span className="chipValue">EVADE</span>
        </Chip>
      ),
    });
  }

  if (blackhandUi.pressurePointActive) {
    player.push({
      key: 'blackhandPressurePoint',
      node: (
        <Chip
          chipKey="blackhandPressurePoint"
          tone="debuff"
          className="chip--blackhand chip--pressurePoint"
          title={blackhandUi.pressurePointMarked
            ? 'Pressure Point: chain overload also costs HP this turn, and because you are Marked those overload costs are doubled.'
            : 'Pressure Point: chain overload also costs HP this turn.'}
        >
          <Icon name="pressurePoint" tone="debuff" />
          <span className="chipValue">HP</span>
          {blackhandUi.pressurePointMarked ? <span className="chipSub">×2</span> : null}
        </Chip>
      ),
    });
  }

  // ----------------------------
  // Turn flow status (player)
  // ----------------------------
  const discardRemaining = playerStatus.pendingDiscardRemaining;
  if (playerStatus.phase === 'player' && discardRemaining > 0) {
    player.push({
      key: 'discard',
      node: (
        <Chip chipKey="discard" title={`Discard ${discardRemaining} card(s)`}>
          <Icon name="discard" />
          <span className="chipValue">{discardRemaining}</span>
        </Chip>
      ),
    });
  } else if (playerStatus.phase === 'player' && playerStatus.canPlay && playerStatus.playerPlaysThisTurn > 0) {
    player.push({
      key: 'chain',
      node: (
        <Chip chipKey="chain" title="Chain active (you may play another card)">
          <Icon name="chain" />
        </Chip>
      ),
    });
  }

  // ----------------------------
  // Delayed effects (player)
  // ----------------------------
  if (playerStatus.delayedCount > 0) {
    const min = playerStatus.delayedMinTurns;
    player.push({
      key: 'delayed',
      node: (
        <Chip chipKey="delayed" title={`${playerStatus.delayedCount} delayed effect(s), next triggers in ${min} turn(s)`}>
          <Icon name="delayed" />
          <span className="chipValue">{playerStatus.delayedCount}</span>
          <span className="chipSub">{min}</span>
        </Chip>
      ),
    });
  }

  // ----------------------------
  // Deck escalation (player)
  // ----------------------------
  if (playerStatus.physicalEscalation > 0) {
    player.push({
      key: 'physEsc',
      node: (
        <Chip chipKey="physEsc" title={`Physical reshuffles: +${playerStatus.physicalEscalation} HP cost`}>
          <Icon name="hp" />
          <span className="chipValue">+{playerStatus.physicalEscalation}</span>
        </Chip>
      ),
    });
  }
  if (playerStatus.magicEscalation > 0) {
    player.push({
      key: 'magEsc',
      node: (
        <Chip chipKey="magEsc" title={`Magic reshuffles: +${playerStatus.magicEscalation} SP cost`}>
          <Icon name="sp" />
          <span className="chipValue">+{playerStatus.magicEscalation}</span>
        </Chip>
      ),
    });
  }

  const items =
    scope === 'enemy'
      ? enemy
      : scope === 'player'
        ? player
        : [...player, ...enemy];

  if (items.length === 0) return null;

  return (
    <TooltipCtx.Provider value={controller}>
      {tooltipPortal}
      {layout === 'arc' ? renderArc(items, scope === 'player' ? 'player' : 'default') : renderInline(items)}
    </TooltipCtx.Provider>
  );
}

// Export memoized StatusChips. The custom comparator ensures that only
// relevant changes to the state or pulse maps trigger a re-render.
export default React.memo(StatusChips, areStatusChipsPropsEqualNew);
